import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BookOpen, LogOut, Loader2, Sparkles, Check, PenTool, Wand2 } from "lucide-react";
import { api } from "@/lib/api";

// Fliboe Flip — isolated audiobook home. Reuses /api/auth/* and existing
// /publish page for writing so we ship the shell + AI generator only.
// Owner emails (fliboecom@gmail.com etc.) auto-get lifetime on any login.

const TOKEN = "fliboe_flip_token";
const INTENDED = "fliboe_flip_intended_tier";
function bootstrap() {
  try {
    if (!localStorage.getItem(TOKEN) && localStorage.getItem("fliboe_token")) {
      localStorage.setItem(TOKEN, localStorage.getItem("fliboe_token"));
    }
  } catch (_) {}
}
if (typeof window !== "undefined") bootstrap();
const auth = () => { bootstrap(); const t = localStorage.getItem(TOKEN); return t ? { Authorization: `Bearer ${t}` } : {}; };

export function FlipLanding() {
  const [tiers, setTiers] = useState([]);
  const [books, setBooks] = useState([]);
  useEffect(() => {
    api.get("/flip/config").then(r => setTiers(r.data?.tiers || [])).catch(() => {});
    api.get("/books").then(r => setBooks((r.data || []).slice(0, 6))).catch(() => {});
  }, []);
  const isSignedIn = typeof window !== "undefined" && !!localStorage.getItem(TOKEN);
  const logout = () => { localStorage.removeItem(TOKEN); localStorage.removeItem("fliboe_token"); window.location.href = "/flip"; };

  return (
    <main className="min-h-screen bg-[#0b0812] text-white relative" data-testid="flip-landing">
      <div className="absolute inset-0 pointer-events-none -z-0">
        <img src="https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=1600&q=80" alt="" className="w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-950/60 via-black/80 to-[#0b0812]" />
        <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(ellipse at top left, rgba(16,185,129,0.20), transparent 55%), radial-gradient(ellipse at bottom right, rgba(147,51,234,0.18), transparent 55%)" }} />
      </div>
      <header className="relative z-10 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/flip" className="flex items-center gap-2"><BookOpen className="w-5 h-5 text-emerald-400" /><span className="fl-display font-black tracking-tight">Flip <span className="text-emerald-400 text-[10px] uppercase tracking-widest ml-1">by Fliboe</span></span></Link>
          <div className="flex items-center gap-2">
            {isSignedIn ? <>
              <Link to="/publish" className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="flip-nav-studio"><PenTool className="w-3.5 h-3.5" /> Studio</Link>
              <button onClick={logout} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="flip-nav-logout"><LogOut className="w-3.5 h-3.5" /> Log out</button>
            </> : <>
              <Link to="/flip/login" className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="flip-nav-login">Sign in</Link>
              <Link to="/flip/login?mode=signup" className="fl-btn !bg-emerald-500 !text-black !py-1.5 !px-3 text-xs" data-testid="flip-nav-signup">Get started</Link>
            </>}
          </div>
        </div>
      </header>

      <section className="relative z-10 max-w-5xl mx-auto px-5 pt-12 pb-8">
        <div className="fl-mono text-[11px] tracking-[0.3em] uppercase text-emerald-400">// Fliboe Flip · your audiobook home</div>
        <h1 className="fl-display text-4xl sm:text-6xl font-black mt-2 tracking-tight leading-[1.02]">Write it. Narrate it.<br/><span className="text-emerald-400">Keep 100% of tips.</span></h1>
        <p className="text-slate-300 mt-4 max-w-2xl">Two ways to publish an audiobook — start from your own manuscript, or let AI draft it from a single prompt. Then narrate with 16 languages, sell PDFs, and share links that Google actually finds.</p>
        <div className="mt-5 flex flex-wrap gap-3">
          <Link to="/flip/write" className="fl-btn !bg-emerald-500 !text-black" data-testid="flip-write-cta"><Sparkles className="w-4 h-4" /> Start writing</Link>
          <Link to="/flip/login?mode=signup" className="fl-btn ghost" data-testid="flip-signup-cta">Sign up free</Link>
        </div>
      </section>

      {/* Tiers */}
      <section className="relative z-10 max-w-5xl mx-auto px-5 pb-10">
        <div className="fl-mono text-[10px] uppercase tracking-widest text-white/60 mb-4">// Tiers</div>
        <div className="grid md:grid-cols-3 gap-4">
          {tiers.map(t => (
            <div key={t.id} className={`fl-card p-5 ${t.highlight ? "bg-emerald-500/10 border-emerald-400/40" : "bg-white/[0.02] border-white/10"}`} data-testid={`flip-tier-${t.id}`}>
              <div className="fl-mono text-[10px] uppercase tracking-widest text-emerald-400">{t.highlight ? "Most popular" : "Plan"}</div>
              <div className="fl-display text-xl font-black mt-2">{t.label}</div>
              <div className="text-emerald-300 font-semibold mt-1">{t.price}</div>
              <ul className="mt-3 space-y-1.5 text-xs text-slate-300">
                {(t.perks || []).map((p, i) => <li key={i} className="flex gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-400 mt-0.5" />{p}</li>)}
              </ul>
              <Link to={`/flip/login?mode=signup&tier=${t.id}`} className={`mt-4 fl-btn ${t.highlight ? "!bg-emerald-500 !text-black" : "ghost"} !py-1.5 !px-3 text-xs w-full justify-center`} data-testid={`flip-tier-cta-${t.id}`}>Choose {t.label}</Link>
            </div>
          ))}
        </div>
      </section>

      {/* Recent public books */}
      {books.length > 0 && (
        <section className="relative z-10 max-w-5xl mx-auto px-5 pb-16">
          <div className="fl-mono text-[10px] uppercase tracking-widest text-white/60 mb-3">// Fresh on Flip</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {books.map(b => (
              <Link key={b.book_id} to={`/book/${b.slug || b.book_id}`} className="fl-card p-3 bg-white/[0.02] border-white/10 hover:border-emerald-400/40 transition block" data-testid={`flip-book-${b.book_id}`}>
                <div className="aspect-[3/4] rounded-lg overflow-hidden bg-gradient-to-br from-emerald-500/20 to-purple-500/20 border border-white/10 mb-2 flex items-center justify-center">
                  {b.cover_url ? (
                    <img src={b.cover_url} alt={b.title} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <BookOpen className="w-10 h-10 text-emerald-400/40" />
                  )}
                </div>
                <div className="text-white font-semibold text-sm leading-snug line-clamp-2">{b.title}</div>
                <div className="text-xs text-slate-400 mt-0.5 truncate">by {b.author_name}</div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}

// ────────────────────────────────────────────────────────────
// FLIP WRITE — the fork: AI or Original
// ────────────────────────────────────────────────────────────
export function FlipWrite() {
  const nav = useNavigate();
  const isSignedIn = typeof window !== "undefined" && !!localStorage.getItem(TOKEN);
  useEffect(() => { if (!isSignedIn) nav("/flip/login?mode=signup&next=/flip/write", { replace: true }); }, [isSignedIn, nav]);
  const [mode, setMode] = useState(null); // "ai" | "original"
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ prompt: "", author_name: "", genre: "novel", tone: "engaging, cinematic", chapter_count: 5, language: "english", voice: "onyx" });

  const runAI = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/author/books/ai-generate", form, { headers: auth(), timeout: 180000 });
      const bookId = data?.book?.book_id;
      if (!bookId) throw new Error("No book id");
      nav(`/publish?book=${bookId}`, { replace: true });
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(Array.isArray(d) ? d.map(x => x?.msg || JSON.stringify(x)).join(" · ") : (typeof d === "string" ? d : "AI draft failed. Try again."));
    } finally { setBusy(false); }
  };

  return (
    <main className="min-h-screen bg-[#0b0812] text-white" data-testid="flip-write">
      <header className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/flip" className="flex items-center gap-2"><BookOpen className="w-5 h-5 text-emerald-400" /><span className="fl-display font-black">Flip · New book</span></Link>
          <Link to="/publish" className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="flip-write-existing">Existing books</Link>
        </div>
      </header>
      <section className="max-w-3xl mx-auto px-5 py-10">
        {!mode ? (
          <div>
            <div className="fl-mono text-[11px] uppercase tracking-widest text-emerald-400">// How do you want to write?</div>
            <h1 className="fl-display text-3xl font-black mt-2">Two paths. Same 100% of tips.</h1>
            <div className="mt-6 grid md:grid-cols-2 gap-4">
              <button onClick={() => setMode("ai")} className="fl-card p-6 text-left bg-emerald-500/10 border-emerald-400/30 hover:border-emerald-400/60 transition" data-testid="flip-mode-ai">
                <Wand2 className="w-5 h-5 text-emerald-400" />
                <div className="fl-display text-xl font-black mt-2">AI-generated</div>
                <p className="text-sm text-slate-300 mt-2">Paste an idea or premise. Claude drafts the outline + every chapter in your chosen language. Edit or narrate as-is.</p>
                <div className="mt-3 text-[10px] fl-mono uppercase tracking-widest text-emerald-300">Fastest · ~90 seconds</div>
              </button>
              <button onClick={() => setMode("original")} className="fl-card p-6 text-left bg-white/[0.02] border-white/10 hover:border-white/30 transition" data-testid="flip-mode-original">
                <PenTool className="w-5 h-5 text-emerald-400" />
                <div className="fl-display text-xl font-black mt-2">Original</div>
                <p className="text-sm text-slate-300 mt-2">Write your own manuscript chapter by chapter in the Studio editor. Save drafts, narrate any chapter with one tap, edit anytime.</p>
                <div className="mt-3 text-[10px] fl-mono uppercase tracking-widest text-white/60">Full control · at your pace</div>
              </button>
            </div>
          </div>
        ) : mode === "ai" ? (
          <div className="fl-card p-6 bg-black/40 border-emerald-400/20" data-testid="flip-ai-form">
            <div className="flex items-center justify-between">
              <div className="fl-mono text-[11px] uppercase tracking-widest text-emerald-400">// AI Write</div>
              <button onClick={() => setMode(null)} className="text-white/50 hover:text-white text-xs">← back</button>
            </div>
            <h2 className="fl-display text-2xl font-black mt-1">Describe your book.</h2>
            <div className="mt-4 grid gap-3">
              <textarea className="fl-input min-h-[120px]" value={form.prompt} onChange={e => setForm({ ...form, prompt: e.target.value })} placeholder="e.g. A retired Nepali radio operator hears voices in the static that only he understands. When one of them warns him about a landslide, he must decide who to trust." data-testid="flip-ai-prompt" />
              <div className="grid sm:grid-cols-2 gap-3">
                <input className="fl-input" placeholder="Author name (yours)" value={form.author_name} onChange={e => setForm({ ...form, author_name: e.target.value })} data-testid="flip-ai-author" />
                <input className="fl-input" placeholder="Genre (novel, memoir, sci-fi…)" value={form.genre} onChange={e => setForm({ ...form, genre: e.target.value })} data-testid="flip-ai-genre" />
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <input className="fl-input" placeholder="Tone" value={form.tone} onChange={e => setForm({ ...form, tone: e.target.value })} data-testid="flip-ai-tone" />
                <select className="fl-input" value={form.language} onChange={e => setForm({ ...form, language: e.target.value })} data-testid="flip-ai-language">
                  {[
                    ["english","English"],
                    ["spanish","Español"],
                    ["french","Français"],
                    ["german","Deutsch"],
                    ["hindi","हिन्दी (Hindi)"],
                    ["gujarati","ગુજરાતી (Gujarati)"],
                    ["portuguese","Português"],
                    ["italian","Italiano"],
                    ["japanese","日本語 (Japanese)"],
                    ["korean","한국어 (Korean)"],
                    ["russian","Русский (Russian)"],
                    ["arabic","العربية (Arabic)"],
                    ["chinese","中文 (Chinese)"],
                    ["polish","Polski"],
                    ["dutch","Nederlands"],
                    ["turkish","Türkçe"],
                    ["swahili","Kiswahili"],
                    ["mandarin","中文 (Mandarin)"],
                  ].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <input type="number" min={1} max={12} className="fl-input" placeholder="Chapters" value={form.chapter_count} onChange={e => setForm({ ...form, chapter_count: Number(e.target.value) || 5 })} data-testid="flip-ai-count" />
              </div>
              {err && <div className="text-red-300 text-sm" data-testid="flip-ai-err">{err}</div>}
              <button onClick={runAI} disabled={busy || !form.prompt.trim() || !form.author_name.trim()} className="fl-btn !bg-emerald-500 !text-black" data-testid="flip-ai-run">
                {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Drafting (60-120s)…</> : <><Wand2 className="w-4 h-4" /> Draft with Claude</>}
              </button>
              <div className="text-[11px] fl-mono uppercase tracking-widest text-white/40">You&apos;ll be able to edit every chapter before publishing.</div>
            </div>
          </div>
        ) : (
          <div className="fl-card p-6 bg-black/40 border-white/20 text-center" data-testid="flip-original-cta">
            <PenTool className="w-8 h-8 text-emerald-400 mx-auto" />
            <h2 className="fl-display text-2xl font-black mt-3">Open the Studio</h2>
            <p className="text-slate-300 text-sm mt-2 max-w-md mx-auto">Create your book title, then add chapters — the editor autosaves your text, lets you narrate any chapter with one tap, and shows a live word count.</p>
            <Link to="/publish" className="fl-btn !bg-emerald-500 !text-black mt-5 inline-flex" data-testid="flip-original-open">Open Studio</Link>
            <button onClick={() => setMode(null)} className="block mx-auto mt-3 text-white/40 hover:text-white text-xs">← back</button>
          </div>
        )}
      </section>
    </main>
  );
}

// ────────────────────────────────────────────────────────────
// FLIP LOGIN / SIGNUP / FORGOT PASSWORD  /flip/login
// ────────────────────────────────────────────────────────────
export function FlipLogin() {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const initial = params.get("mode") === "signup" ? "signup" : "login";
  const nextPath = params.get("next") || "/flip/write";
  const tierParam = params.get("tier");
  const [mode, setMode] = useState(initial);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [forgotOk, setForgotOk] = useState(false);
  useEffect(() => { if (tierParam) localStorage.setItem(INTENDED, tierParam); }, [tierParam]);

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (mode === "forgot") {
      if (!email.trim()) { setErr("Enter your email."); return; }
      setBusy(true);
      try { await api.post("/auth/forgot-password", { email }); setForgotOk(true); }
      catch (ex) { setErr(ex?.response?.data?.detail || "Could not send reset email."); }
      finally { setBusy(false); }
      return;
    }
    if (!email.trim() || password.length < 8) { setErr("Email + 8-char password required."); return; }
    setBusy(true);
    try {
      const path = mode === "signup" ? "/auth/register" : "/auth/login";
      const body = mode === "signup"
        ? { email, password, name: (name || email.split("@")[0] || "Flip Author").trim() }
        : { email, password };
      const { data } = await api.post(path, body);
      if (!data.session_token) throw new Error("No token");
      localStorage.setItem(TOKEN, data.session_token);
      navigate(nextPath, { replace: true });
    } catch (ex) {
      const d = ex?.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map(x => x?.msg || JSON.stringify(x)).join(" · ") : (typeof d === "string" ? d : (ex?.message || "Sign-in failed."));
      setErr(msg);
    } finally { setBusy(false); }
  };

  return (
    <main className="min-h-screen bg-[#0b0812] text-white grid place-items-center p-5" data-testid="flip-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="fl-mono text-[11px] uppercase tracking-[0.3em] text-emerald-400">// Fliboe Flip</div>
          <h1 className="fl-display text-3xl font-black mt-2">
            {mode === "signup" ? "Create your Flip account" : mode === "forgot" ? "Reset your password" : "Welcome back"}
          </h1>
          {mode === "signup" && tierParam && <div className="mt-2 text-xs text-emerald-300">Selected plan: <strong>{tierParam.replace("flip_", "").replace("_", " + ")}</strong></div>}
        </div>
        <form onSubmit={submit} className="fl-card p-5 space-y-3 bg-black/60 border-emerald-400/20">
          {mode === "forgot" && forgotOk ? (
            <div className="text-emerald-300 text-sm text-center py-4" data-testid="flip-forgot-ok">
              If <strong>{email}</strong> matches an account, we&apos;ve emailed a reset link.
              <button type="button" onClick={() => { setMode("login"); setForgotOk(false); setErr(""); }} className="block mx-auto mt-4 text-emerald-300 text-xs hover:underline">Back to sign in</button>
            </div>
          ) : (
            <>
              {mode === "signup" && (
                <input className="fl-input w-full" placeholder="Author name (optional)" value={name} onChange={e => setName(e.target.value)} data-testid="flip-name" />
              )}
              <input className="fl-input w-full" placeholder="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} data-testid="flip-email" />
              {mode !== "forgot" && (
                <input className="fl-input w-full" placeholder="Password (8+ chars)" type="password" value={password} onChange={e => setPassword(e.target.value)} data-testid="flip-password" />
              )}
              {err && <div className="text-red-400 text-sm" data-testid="flip-err">{err}</div>}
              <button type="submit" disabled={busy} className="fl-btn w-full justify-center !bg-emerald-500 !text-black hover:!bg-emerald-400" data-testid="flip-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {mode === "signup" ? "Create & continue" : mode === "forgot" ? "Send reset email" : "Sign in"}
              </button>
              {mode !== "forgot" && (<>
                <div className="flex items-center gap-3 my-1">
                  <div className="flex-1 h-px bg-white/10" />
                  <span className="text-white/40 text-[10px] font-mono uppercase tracking-widest">or</span>
                  <div className="flex-1 h-px bg-white/10" />
                </div>
                <button type="button" onClick={() => {
                  const redirect = window.location.origin + "/flip/auth-callback?next=" + encodeURIComponent(nextPath);
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
                }} className="w-full py-2.5 rounded-lg bg-white text-black font-semibold hover:bg-white/90 flex items-center justify-center gap-2 transition" data-testid="flip-google">
                  <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC04" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                  Continue with Google
                </button>
              </>)}
              <div className="flex items-center justify-between gap-3 mt-1">
                <button type="button" onClick={() => { setMode(mode === "signup" ? "login" : "signup"); setErr(""); }} className="text-emerald-300 text-xs hover:underline" data-testid="flip-toggle">
                  {mode === "signup" ? "Have an account? Sign in" : mode === "forgot" ? "Back to sign in" : "New here? Create account"}
                </button>
                {mode !== "forgot" && (
                  <button type="button" onClick={() => { setMode("forgot"); setErr(""); }} className="text-white/50 text-xs hover:text-emerald-300 hover:underline" data-testid="flip-forgot">
                    Forgot password?
                  </button>
                )}
              </div>
            </>
          )}
        </form>
        <div className="text-center mt-4">
          <Link to="/flip" className="text-white/40 text-xs hover:text-white">← Back to Flip</Link>
        </div>
      </div>
    </main>
  );
}

export function FlipAuthCallback() {
  const navigate = useNavigate();
  const done = React.useRef(false);
  React.useEffect(() => {
    if (done.current) return; done.current = true;
    (async () => {
      const m = (window.location.hash || "").match(/session_id=([^&]+)/);
      const next = new URLSearchParams(window.location.search).get("next") || "/flip/write";
      if (!m) { navigate("/flip/login", { replace: true }); return; }
      try {
        const { data } = await api.post("/auth/session", { session_id: m[1] });
        if (!data?.session_token) throw new Error("no token");
        localStorage.setItem(TOKEN, data.session_token);
        window.history.replaceState({}, document.title, next);
        navigate(next, { replace: true });
      } catch { navigate("/flip/login", { replace: true }); }
    })();
  }, [navigate]);
  return <main className="min-h-screen bg-[#0b0812] text-white grid place-items-center" data-testid="flip-auth-cb"><div className="fl-mono text-xs uppercase tracking-widest text-emerald-300">// Signing you in…</div></main>;
}

